/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx } from "@emotion/core";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { AccountReceipt } from "containers/account/receipt/AccountReceipt";
import { useI18n } from "hooks/i18n/i18n";
import { Link } from "react-router-dom";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { SubContentsLayout } from "ui/SubContentsLayout";

import { OrderInfo } from "./components/OrderInfo";
import { ProductInfo } from "./components/ProductInfo";
import { ActionButtonArea } from "./components/ProductInfo/components/ActionButtonArea";
import { PRODUCT_TYPE } from "./constants/ProductType";
import { TICKET_CLASS } from "./constants/TicketClass";
import {
  blackActionButtonStyle,
  containerStyle,
  contentWrapperStyle,
  informationStyle,
  skeletonButtonStyle,
  titleStyle,
  whiteActionButtonStyle,
} from "./styles";

import type { DeliveryData } from "./types/DeliveryData";
import type { FulfillmentData } from "./types/FulfillmentData";
import type { OrderData } from "./types/OrderData";
import type { ProductItem } from "./types/ProductItem";
import type { ReceiptData } from "containers/account/receipt/AccountReceipt";
import type { FC } from "react";
import type { Options } from "utility/isFeatureEnabled/types/options";

type FeatureFlagName = Options["featureName"];

type Props = {
  deliveryData: DeliveryData | null;
  fulfillmentData: FulfillmentData | null;
  handleCloseReceipt: () => void;
  handleCopyOrderId: (orderId: number) => void;
  handleShowReceipt: () => void;
  isDisplayReceipt: boolean;
  isLoadingAddress: boolean;
  isLoadingBonus: boolean;
  isLoadingFulfillment: boolean;
  orderData: OrderData | null;
  orderList: ProductItem[];
  receiptData: ReceiptData;
};

export const View: FC<Props> = (props) => {
  const {
    deliveryData,
    fulfillmentData,
    handleCloseReceipt,
    handleCopyOrderId,
    handleShowReceipt,
    isDisplayReceipt,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    orderData,
    orderList,
    receiptData,
  } = props;

  const { t } = useI18n();

  if (isDisplayReceipt) {
    return (
      <AccountReceipt
        receiptData={receiptData}
        resetState={handleCloseReceipt}
      />
    );
  }

  if (!orderData) {
    return <LoaderLayout />;
  }

  const isLiveStream = (item: ProductItem) => {
    if (item.type !== PRODUCT_TYPE.TICKET) return false;
    return (
      item.liveTicketInfo.class === TICKET_CLASS.LIVE_STREAM ||
      item.liveTicketInfo.class === TICKET_CLASS.VOD_STREAM
    );
  };

  const ActionButton = ({ item }: { item: ProductItem }) => {
    const isTicket = item.type === PRODUCT_TYPE.TICKET;
    const isLiveStreaming = isLiveStream(item);
    const ticketHref =
      isTicket && isLiveStreaming
        ? `/events/${item.liveTicketInfo?.eventId}/streaming`
        : "/account/ticket";
    const ticketLabel = isLiveStreaming
      ? t("settlementHistory.ticketLabel.streaming")
      : t("settlementHistory.ticketLabel.ticket");
    const FEATURE_FLAG_NAME: FeatureFlagName = "newPurchaseHistory";

    switch (item.type) {
      case PRODUCT_TYPE.TICKET:
        return (
          <ActionButtonArea>
            <Link to={ticketHref} css={blackActionButtonStyle}>
              {ticketLabel}
            </Link>
            {isLoadingBonus ? (
              <div css={skeletonButtonStyle} />
            ) : (
              item.liveTicketInfo.hasBonusMedia && (
                <Link
                  // TODO: 限定公開が不要になったら削除する
                  // https://github.com/balus-co-ltd/spwn/issues/10899
                  to={`/account/settlement/history/${orderData?.orderId}/items/${item.liveTicketInfo.ticketId}/downloads?events=${item.liveTicketInfo.eventId}&${FEATURE_FLAG_NAME}`}
                  css={blackActionButtonStyle}
                >
                  {t("settlementHistory.digitalBonusDownload")}
                </Link>
              )
            )}
            <Link
              to={`/events/${item.liveTicketInfo.eventId}`}
              css={whiteActionButtonStyle}
            >
              {t("settlementHistory.eventPage")}
            </Link>
          </ActionButtonArea>
        );
      default:
        return null;
    }
  };

  return (
    <SubContentsLayout>
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          { label: t("common.routes.account"), link: "/account" },
          { label: t("settlementHistory.breadcrumb.orderDetails"), link: null },
        ]}
      />
      <div css={containerStyle}>
        <div css={contentWrapperStyle}>
          <h1 css={titleStyle}>{t("settlementHistory.pageTitle")}</h1>
          <div css={informationStyle}>
            <OrderInfo
              deliveryData={deliveryData ?? undefined}
              fulfillmentData={fulfillmentData ?? undefined}
              isLoadingAddress={isLoadingAddress}
              isLoadingFulfillment={isLoadingFulfillment}
              onCopyOrderId={handleCopyOrderId}
              onShowReceipt={handleShowReceipt}
              orderData={orderData}
            />
            {orderList.map((item) => (
              <div key={`${item.productName}-${item.variationName}`}>
                <ProductInfo {...item} />
                <ActionButton item={item} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SubContentsLayout>
  );
};
